import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../services/supabase';
import TyreInventoryCard from './cards/TyreInventoryCard';
import InventoryFilters from './InventoryFilters';
import InventorySearch from './InventorySearch';

interface TyreInventoryItem {
  id: string;
  brand: string;
  model: string;
  size: string;
  type: string;
  quantity: number;
  image_url?: string;
  width?: string;
  profile?: string;
  rim_size?: string;
  load_index?: string;
  speed_rating?: string;
  features?: string[];
}

interface InventorySectionProps {
  businessId: string;
  businessName?: string;
  businessPhone?: string;
  businessSlug?: string;
  // Booking/messaging/quote system is hidden for now - these are kept in the
  // interface only so ShopPage.tsx (which still passes them) doesn't break
  // until that file is updated too. No longer used here.
  onMessage?: (tyre: TyreInventoryItem) => void;
  onBookFitting?: (tyre: TyreInventoryItem) => void;
  onRequestQuote?: (tyre: TyreInventoryItem) => void;
}

const InventorySection: React.FC<InventorySectionProps> = ({
  businessId
}) => {
  const [inventory, setInventory] = useState<TyreInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedStockStatus, setSelectedStockStatus] = useState('all');

  // Fetch inventory data
  useEffect(() => {
    const fetchInventory = async () => {
      if (!businessId) return;
      
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('tyre_inventory')
          .select('*')
          .eq('business_id', businessId)
          .order('brand', { ascending: true });

        if (fetchError) {
          setError('Failed to load inventory');
          console.error('Inventory fetch error:', fetchError);
        } else {
          setInventory(data || []);
        }
      } catch (err) {
        setError('An unexpected error occurred');
        console.error('Inventory fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [businessId]);

  // Generate filter options from inventory data
  const filterOptions = useMemo(() => {
    const brands = [...new Set(inventory.map(item => item.brand))].sort();
    const types = [...new Set(inventory.map(item => item.type))].filter(Boolean).sort();
    const sizes = [...new Set(inventory.map(item => item.size))].filter(Boolean).sort();

    return { brands, types, sizes, stockStatus: 'all' as const };
  }, [inventory]);

  // Filter and search inventory
  const filteredInventory = useMemo(() => {
    let filtered = inventory;

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.brand.toLowerCase().includes(query) ||
        item.model.toLowerCase().includes(query) ||
        item.size.toLowerCase().includes(query) ||
        item.type.toLowerCase().includes(query) ||
        item.load_index?.toLowerCase().includes(query) ||
        item.speed_rating?.toLowerCase().includes(query)
      );
    }

    // Zero-stock items are never shown to customers
    filtered = filtered.filter(item => item.quantity > 0);

    // Apply filters
    if (selectedBrand) {
      filtered = filtered.filter(item => item.brand === selectedBrand);
    }

    if (selectedType) {
      filtered = filtered.filter(item => item.type === selectedType);
    }

    if (selectedSize) {
      filtered = filtered.filter(item => item.size === selectedSize);
    }

    if (selectedStockStatus !== 'all') {
      filtered = filtered.filter(item => {
        switch (selectedStockStatus) {
          case 'in_stock':
            return item.quantity > 5;
          case 'low_stock':
            return item.quantity > 0 && item.quantity <= 5;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [inventory, searchQuery, selectedBrand, selectedType, selectedSize, selectedStockStatus]);

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedBrand('');
    setSelectedType('');
    setSelectedSize('');
    setSelectedStockStatus('all');
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
          <span>🛞</span>Inventory
        </h2>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
          <span>🛞</span>Inventory
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (inventory.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
          <span>🛞</span>Inventory
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-600">No inventory available at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
        <span>🛞</span>Inventory
        <span className="text-sm font-normal text-gray-600 ml-2">
          ({filteredInventory.length} of {inventory.length} tyres)
        </span>
      </h2>

      {/* Search */}
      <div className="mb-4">
        <InventorySearch 
          onSearch={setSearchQuery}
          placeholder="Search tyres by brand, model, size, or specs..."
        />
      </div>

      {/* Filters */}
      <InventoryFilters
        filters={filterOptions}
        selectedBrand={selectedBrand}
        selectedType={selectedType}
        selectedSize={selectedSize}
        selectedStockStatus={selectedStockStatus}
        onBrandChange={setSelectedBrand}
        onTypeChange={setSelectedType}
        onSizeChange={setSelectedSize}
        onStockStatusChange={setSelectedStockStatus}
        onClearFilters={handleClearFilters}
      />

      {/* Inventory Grid */}
      {filteredInventory.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">
            {searchQuery || selectedBrand || selectedType || selectedSize || selectedStockStatus !== 'all'
              ? 'No tyres match your search criteria.'
              : 'No tyres available.'
            }
          </p>
          {(searchQuery || selectedBrand || selectedType || selectedSize || selectedStockStatus !== 'all') && (
            <button
              onClick={handleClearFilters}
              className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInventory.map((tyre) => (
            <TyreInventoryCard
              key={tyre.id}
              tyre={tyre}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InventorySection;
